import { getTeableConfig, isTeableConfigured } from '@/config/teableConfig';
import { getPromoTableId, getTableRecords, createTableRecord, updateTableRecord, deleteTableRecord, getHeaders } from '@/services/teableApi';
import { italianProvinces, provinceLimits } from '@/utils/mockData';
import type { TeableRecord } from '@/types/teable';
import { cacheService } from './cacheService';
import { secureLog } from '@/utils/secureLogger';
import { sanitizeInput, validateLength } from '@/utils/inputValidation';

/**
 * Funzione helper per verificare la corrispondenza della provincia
 */
function checkProvinceMatch(recordProvincia: string, targetProvince: string): boolean {
  if (!recordProvincia) return false;
  
  // Normalizza entrambe le stringhe
  const recordNormalized = recordProvincia.toLowerCase().trim();
  const targetNormalized = targetProvince.toLowerCase().trim();
  
  // Strategia 1: Confronto diretto
  if (recordNormalized === targetNormalized) return true;
  
  // Strategia 2: Rimuovi numeri finali e confronta
  const recordBase = recordNormalized.replace(/\d+$/, '').trim();
  if (recordBase === targetNormalized) return true;
  
  // Strategia 3: Verifica se la provincia target è contenuta nel record
  if (recordNormalized.includes(targetNormalized)) return true;
  
  // Strategia 4: Verifica se il record è contenuto nella provincia target
  if (targetNormalized.includes(recordBase)) return true;
  
  return false;
}

/**
 * Recupera i record di una provincia specifica usando getTableRecords
 */
export const getAllRecordsForProvince = async (
  tableId: string, 
  provinciaFilter: string
): Promise<any[]> => {
  // Validazione input
  const sanitizedTableId = sanitizeInput(tableId);
  const sanitizedProvince = sanitizeInput(provinciaFilter);
  
  if (!sanitizedTableId || !sanitizedProvince) {
    throw new Error('Parametri non validi');
  }

  const provincia = italianProvinces.find(p => p.id === sanitizedProvince);
  if (!provincia) {
    secureLog.error(`Provincia non trovata per sigla: ${sanitizedProvince}`);
    return [];
  }

  try {
    // Usa getTableRecords con filtro base (verrà wrappato dalla edge function)
    const filter = {
      fieldId: 'Provincia',
      operator: 'is',
      value: provincia.name
    };
    
    const allRecords = await getTableRecords(sanitizedTableId, {
      filter: filter,
      sort: [{ field: 'Posizione', order: 'asc' }]
    });
    
    return allRecords;
  } catch (error) {
    secureLog.error(`Errore nel recupero record per provincia ${sanitizedProvince}:`, error);
    return [];
  }
};

/**
 * Trova la prossima posizione libera per una provincia specifica
 */
export const findNextPosition = async (tableName: string, provinciaFilter: string): Promise<number> => {
  try {
    const tableId = await getPromoTableId(tableName);
    
    // Trova il nome della provincia dalla sigla
    const provincia = italianProvinces.find(p => p.id === provinciaFilter);
    if (!provincia) {
      return 1;
    }
    
    // Ottieni TUTTI i record di questa provincia usando il filtro diretto
    const validRecords = await getAllRecordsForProvince(tableId, provinciaFilter);
    
    // Estrae le posizioni già occupate
    const occupiedPositions = validRecords
      .map((record: any) => {
        const pos = parseInt(record.fields.Posizione);
        return !isNaN(pos) ? pos : null;
      })
      .filter((pos: number | null) => pos !== null)
      .sort((a: number, b: number) => a - b);
    
    // Trova la prima posizione libera o il primo record vuoto
    let nextPosition = 1;
    
    // Controlla se c'è già un record vuoto (senza dati) per riempirlo
    const emptyRecord = validRecords.find((record: any) => {
      const hasData = !!(record.fields.Nome1 || record.fields.Località || record.fields.Insegna);
      return !hasData;
    });
    
    if (emptyRecord) {
      const emptyPosition = parseInt(emptyRecord.fields.Posizione);
      if (!isNaN(emptyPosition)) {
        return emptyPosition;
      }
    }
    
    // Se non ci sono record vuoti, trova la prima posizione numerica libera
    for (const position of occupiedPositions) {
      if (position === nextPosition) {
        nextPosition++;
      } else {
        break;
      }
    }
    
    return nextPosition;
  } catch (error) {
    return 1;
  }
};

/**
 * Recupera le ultime 3 promozioni inserite (ordinate per DataCreazione)
 * Filtra le promozioni scadute (DataFine)
 */
export const fetchLatestPromos = async (): Promise<TeableRecord[]> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  try {
    const config = getTeableConfig();
    let url = `${config.baseUrl}/table/${config.promoTableId}/records`;
    
    // Ordina per DataCreazione in ordine decrescente e limita a 3 risultati
    const queryParams = new URLSearchParams();
    queryParams.append('sort', JSON.stringify([{ field: 'DataCreazione', order: 'desc' }]));
    queryParams.append('maxRecords', '10'); // Prendiamo più record per filtrare quelli scaduti
    url += `?${queryParams.toString()}`;

    const allRecords = await getTableRecords(config.promoTableId, {
      sort: [{ field: 'DataCreazione', order: 'desc' }],
      maxRecords: 10
    });
    
    // Filtra le promozioni scadute
    const today = new Date();
    const activePromos = allRecords.filter((record: any) => {
      const dataFine = record.fields.DataFine;
      if (!dataFine) return true; // Se non c'è DataFine, la promo è sempre attiva
      
      // Converte il formato italiano dd/mm/yyyy in mm/dd/yyyy per JavaScript
      const dateParts = dataFine.split('/');
      if (dateParts.length === 3) {
        const [day, month, year] = dateParts;
        const endDate = new Date(`${month}/${day}/${year}`);
        return endDate >= today;
      }
      return true;
    });
    
    // Restituisce solo le prime 3 promozioni attive
    return activePromos.slice(0, 3);
  } catch (error) {
    return [];
  }
};

/**
 * Recupera i prodotti featured da Amazon (max 5)
 * NOTA: L'utente deve creare una tabella "ProdottiFeatured" su Teable con i seguenti campi:
 * - Nome (text)
 * - Immagine (attachment o text url)
 * - Prezzo (text)
 * - LinkAmazon (url)
 * - Ordine (number)
 * - Attivo (checkbox)
 */
export const fetchFeaturedProducts = async (): Promise<TeableRecord[]> => {
  try {
    // ID tabella ProdottiFeatured su Teable
    const FEATURED_TABLE_ID = 'tblRrpbbB9kSrvPJweR';
    
    const allRecords = await getTableRecords(FEATURED_TABLE_ID, {
      sort: [{ field: 'Ordine', order: 'asc' }],
      maxRecords: 10
    });
    
    return allRecords;
  } catch (error) {
    return [];
  }
};

/**
 * Recupera i dati dalla tabella Promozioni (solo promozioni attive)
 */
export const fetchPromos = async (): Promise<TeableRecord[]> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  const config = getTeableConfig();
  let url = `${config.baseUrl}/table/${config.promoTableId}/records`;

  const allRecords = await getTableRecords(config.promoTableId);
  
  // Filtra le promozioni scadute
  const today = new Date();
  
  const activePromos = allRecords.filter((record: any) => {
    const dataFine = record.fields.DataFine;
    
    if (!dataFine) {
      return true; // Se non c'è DataFine, la promo è sempre attiva
    }
    
    // Converte il formato italiano dd/mm/yyyy in mm/dd/yyyy per JavaScript
    const dateParts = dataFine.trim().split('/');
    if (dateParts.length === 3) {
      const [day, month, year] = dateParts;
      // Assicuriamoci che i numeri siano validi
      const dayNum = parseInt(day, 10);
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);
      
      if (!isNaN(dayNum) && !isNaN(monthNum) && !isNaN(yearNum)) {
        // Crea la data usando il costruttore (year, month-1, day) per evitare problemi di parsing
        const endDate = new Date(yearNum, monthNum - 1, dayNum);
        const isActive = endDate >= today;
        return isActive;
      }
    }
    return true;
  });
  
  return activePromos;
};

/**
 * Recupera i dati dalla tabella segnalazioni (nome dinamico)
 */
export const fetchData = async (tableName: string, filters?: Record<string, any>): Promise<TeableRecord[]> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  try {
    const tableId = await getPromoTableId(tableName);
    const config = getTeableConfig();
    
    let url = `${config.baseUrl}/table/${tableId}/records`;
    
    // Aggiunge filtri alla query se forniti
    if (filters) {
      const queryParams = new URLSearchParams();
      queryParams.append('filterString', JSON.stringify(filters));
      url += `?${queryParams.toString()}`;
    }

    return await getTableRecords(tableId, filters ? { filter: filters } : undefined);
  } catch (error) {
    throw error;
  }
};

/**
 * Recupera i punti vendita per una specifica promozione e provincia (versione ottimizzata)
 */
export const fetchStoresByPromoAndProvince = async (
  tableName: string, 
  promoName: string, 
  provinceId: string
): Promise<TeableRecord[]> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  try {
    const tableId = await getPromoTableId(tableName);
    
    // Trova il nome della provincia dalla sigla
    const provincia = italianProvinces.find(p => p.id === provinceId);
    if (!provincia) {
      return [];
    }
    
    // Usa l'offset ottimizzato per recuperare solo i record della provincia
    const { getProvinceOffset, getProvinceMaxRecords } = await import('@/utils/provinceMapping');
    const provinceOffset = getProvinceOffset(provinceId);
    const maxRecords = getProvinceMaxRecords(provinceId);
    
    // Recupera solo i record della provincia specifica
    const provinceRecords = await getTableRecords(tableId, {
      offset: provinceOffset,
      maxRecords: maxRecords,
      sort: [{ field: 'Posizione', order: 'asc' }]
    });
    
    // Filtra per provincia e promozione con logica migliorata
    const filteredRecords = provinceRecords.filter((record: any) => {
      const recordProvincia = record.fields.Provincia;
      const recordPromo = record.fields.Promo || record.fields.Promozione || record.fields.Nome;
      
      // Verifica provincia con multiple strategie
      const provinceMatch = checkProvinceMatch(recordProvincia, provincia.name);
      
      // Verifica promozione (case-insensitive e partial match)
      const promoMatch = recordPromo && 
        recordPromo.toLowerCase().includes(promoName.toLowerCase());
      
      // Verifica che ci siano dati effettivi
      const hasData = !!(record.fields.Nome1 || record.fields.Località || record.fields.Insegna);
      
      return provinceMatch && promoMatch && hasData;
    });
    
    return filteredRecords;
  } catch (error) {
    throw error;
  }
};

/**
 * Versione ottimizzata di fetchProvinceData con caching migliorato
 */
export const fetchProvinceDataOptimized = async (
  tableName: string, 
  provinceId: string,
  useCache: boolean = true
): Promise<TeableRecord[]> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  const cacheKey = `province_${tableName}_${provinceId}`;
  
  // Usa il cache service unificato
  if (useCache) {
    const cached = cacheService.get<TeableRecord[]>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  try {
    const tableId = await getPromoTableId(tableName);
    
    // Trova il nome della provincia dalla sigla
    const provincia = italianProvinces.find(p => p.id === provinceId);
    if (!provincia) {
      return [];
    }
    
    // USA FILTRO BASE (verrà wrappato dalla edge function)
    const filter = {
      fieldId: 'Provincia',
      operator: 'is',
      value: provincia.name
    };
    
    // Recupera tutti i record della provincia con filtro diretto
    const provinceRecords = await getTableRecords(tableId, {
      filter: filter,
      maxRecords: 100,
      sort: [{ field: 'Posizione', order: 'asc' }]
    });
    
    // Filtra solo quelli con dati effettivi
    const filteredRecords = provinceRecords.filter((record: any) => {
      return !!(record.fields.Nome1 || record.fields.Località || record.fields.Insegna);
    });
    
    // Salva in cache
    if (useCache) {
      cacheService.set(cacheKey, filteredRecords, 3 * 60 * 1000); // 3 minuti
    }
    
    return filteredRecords;
  } catch (error) {
    throw error;
  }
};

/**
 * Debug function per capire la struttura dei dati
 */
export const debugProvinceData = async (tableName: string, provinceId: string): Promise<void> => {
  try {
    const tableId = await getPromoTableId(tableName);
    const provincia = italianProvinces.find(p => p.id === provinceId);
    
    if (!provincia) {
      secureLog.error(`Provincia non trovata: ${provinceId}`);
      return;
    }
    
    const { getProvinceOffset, getProvinceMaxRecords } = await import('@/utils/provinceMapping');
    const provinceOffset = getProvinceOffset(provinceId);
    const maxRecords = getProvinceMaxRecords(provinceId);
    
    secureLog.log('Debug provincia', { 
      provincia: provincia.name,
      offset: provinceOffset, 
      maxRecords 
    });
    
    const provinceRecords = await getTableRecords(tableId, {
      offset: provinceOffset,
      maxRecords: maxRecords
    });
    
    secureLog.log('Record recuperati', { count: provinceRecords.length });
    
    // Analizza i primi 5 record
    provinceRecords.slice(0, 5).forEach((record: any, index: number) => {
      secureLog.log(`Record ${index + 1}`, {
        id: record.id,
        hasData: !!(record.fields.Nome1 || record.fields.Località || record.fields.Insegna)
      });
    });
    
    // Trova tutti i nomi di provincia unici
    const uniqueProvinces = [...new Set(provinceRecords.map((r: any) => r.fields.Provincia))]
      .filter(p => p);
    
    secureLog.log('Province trovate', { count: uniqueProvinces.length });
    
    // Trova tutti i nomi di promo unici
    const uniquePromos = [...new Set(provinceRecords.map((r: any) => 
      r.fields.Promo || r.fields.Promozione || r.fields.Nome
    ))].filter(p => p);
    
    secureLog.log('Promozioni trovate', { count: uniquePromos.length });
    
  } catch (error) {
    secureLog.error('Errore debug provincia', error);
  }
};

/**
 * Recupera i dati di una specifica provincia (con caching ottimizzato)
 */
export const fetchProvinceData = async (tableName: string, provinceId: string): Promise<TeableRecord[]> => {
  return fetchProvinceDataOptimized(tableName, provinceId, true);
};

/**
 * Inserisce un nuovo record nella tabella segnalazioni con paginazione completa
 */
export const createRecord = async (tableName: string, data: any, turnstileToken?: string): Promise<{ record: TeableRecord; position: number }> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  // Verifica Turnstile token se presente (passato alla edge function)
  if (turnstileToken) {
    data._turnstileToken = turnstileToken;
  }

  try {
    const config = getTeableConfig();
    const tableId = await getPromoTableId(tableName);
    
    // Trova la provincia (gestisce sia nome che sigla)
    const provincia = italianProvinces.find(p => 
      p.name === data.provincia || 
      p.id === data.sigla ||
      p.name === data.Provincia ||
      p.id === data.Sigla
    );
    
    if (!provincia) {
      throw new Error(`Provincia "${data.provincia || data.sigla || data.Provincia || data.Sigla}" non trovata nella lista delle province italiane.`);
    }
    
    // Cerca tutti i record esistenti per quella provincia con filtro diretto
    const filter = {
      conjunction: 'and',
      filterSet: [{
        fieldId: 'Provincia',
        operator: 'is',
        value: provincia.name
      }]
    };
    
    const existingRecords = await getTableRecords(tableId, {
      filter: filter,
      maxRecords: 100,
      sort: [{ field: 'Posizione', order: 'asc' }]
    });
    
    // Prima conta quelli che hanno già dati per la posizione corretta
    const recordsWithData = existingRecords.filter((record: any) => 
      record.fields.Nome1 || record.fields.Località || record.fields.Insegna
    );
    
    // Trova il primo record vuoto (senza dati nei campi principali)
    let targetRecord = existingRecords.find((record: any) => 
      !record.fields.Nome1 && 
      !record.fields.Località && 
      !record.fields.Insegna
    );
    
    if (targetRecord) {
      // Prepara i campi nel formato corretto secondo lo schema Teable
      const fields: any = {
        Provincia: provincia.name, // Testo
        Località: data.localita || data.Località, // Testo
        Insegna: data.insegna || data.Insegna, // Testo
        Via: data.via || data.Via, // Testo
        Nome1: data.nome1 || data.Nome1, // Testo
        Ean1: data.ean1 || data.Ean1 || 'N/A', // Testo
        Prezzo1: parseFloat(data.prezzo1 || data.Prezzo1), // Numero decimale
        Posizione: parseInt(targetRecord.fields.Posizione) // Numero intero
      };
      
      // Aggiungi prodotti opzionali
      if (data.nome2 || data.Nome2) {
        fields.Nome2 = data.nome2 || data.Nome2; // Testo
        fields.Ean2 = data.ean2 || data.Ean2 || 'N/A'; // Testo
        fields.Prezzo2 = parseFloat(data.prezzo2 || data.Prezzo2); // Numero decimale
      }
      if (data.nome3 || data.Nome3) {
        fields.Nome3 = data.nome3 || data.Nome3; // Testo
        fields.Ean3 = data.ean3 || data.Ean3 || 'N/A'; // Testo
        fields.Prezzo3 = parseFloat(data.prezzo3 || data.Prezzo3); // Numero decimale
      }
      if (data.nome4 || data.Nome4) {
        fields.Nome4 = data.nome4 || data.Nome4; // Testo
        fields.Ean4 = data.ean4 || data.Ean4 || 'N/A'; // Testo
        fields.Prezzo4 = parseFloat(data.prezzo4 || data.Prezzo4); // Numero decimale
      }
      if (data.nome5 || data.Nome5) {
        fields.Nome5 = data.nome5 || data.Nome5; // Testo
        fields.Ean5 = data.ean5 || data.Ean5 || 'N/A'; // Testo
        fields.Prezzo5 = parseFloat(data.prezzo5 || data.Prezzo5); // Numero decimale
      }
      
      // Aggiungi flag promozioni come stringhe "true" o "false"
      if (data.volantino !== undefined || data.Volantino !== undefined) {
        const vol = data.volantino || data.Volantino;
        fields.Volantino = (vol === true || vol === 'true') ? 'true' : 'false';
      }
      if (data.materiale !== undefined || data.Materiale !== undefined) {
        const mat = data.materiale || data.Materiale;
        fields.Materiale = (mat === true || mat === 'true') ? 'true' : 'false';
      }
      
      // Aggiorna usando updateTableRecord
      const updatedRecord = await updateTableRecord(tableId, targetRecord.id, fields);
      
      // La posizione corretta è il numero di record con dati + 1 (quello appena aggiunto)
      const actualPosition = recordsWithData.length + 1;
      
      return { 
        record: updatedRecord, 
        position: actualPosition
      };
    } else {
      throw new Error('Numero massimo di inserimenti raggiunto per questa provincia.');
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Aggiorna un record esistente nella tabella segnalazioni (nome dinamico)
 */
export const updateRecord = async (tableName: string, recordId: string, data: Partial<any>): Promise<TeableRecord> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  try {
    const tableId = await getPromoTableId(tableName);
    const config = getTeableConfig();
    
    const url = `${config.baseUrl}/table/${tableId}/records`;
    
    return await updateTableRecord(tableId, recordId, data);
  } catch (error) {
    throw error;
  }
};

/**
 * Elimina un record dalla tabella segnalazioni (nome dinamico)
 */
export const deleteRecord = async (tableName: string, recordId: string): Promise<boolean> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non è configurato. Imposta le configurazioni prima di utilizzarlo.');
  }

  try {
    const tableId = await getPromoTableId(tableName);
    const config = getTeableConfig();
    
    const url = `${config.baseUrl}/table/${tableId}/records`;
    
    return await deleteTableRecord(tableId, recordId);
  } catch (error) {
    throw error;
  }
};
